
import { useState, useCallback } from 'react';
import { ItineraryEvent, TransportationDetails } from '@/types/itinerary';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { parseISO, format } from 'date-fns';
import { buildEventDescription } from './utils/transportationUtils';

export const useEditTransportation = (event: ItineraryEvent, onClose: () => void) => {
  const [isSaving, setIsSaving] = useState(false);
  
  // Extract initial values from the event
  const getInitialDetails = useCallback((): TransportationDetails => {
    const locationParts = event.location?.split(' To: ') || [];
    const departure = locationParts[0]?.replace('From: ', '') || '';
    const arrival = locationParts[1] || '';
    
    const flightNumber = event.title.includes('Flight') 
      ? event.title.match(/[A-Z0-9]{2,}\d+/)?.[0] || ''
      : '';
      
    return {
      type: event.transportation_type || 'flight',
      departure,
      arrival,
      departureDate: event.date ? parseISO(event.date) : null,
      departureTime: event.time || '',
      notes: getCleanDescription(event.description || ''),
      confirmation: getConfirmationNumber(event.description || ''),
      userName: event.user_name || '',
      flightNumber,
      confirmations: event.description?.includes('Confirmation:') 
        ? [{ id: '1', value: getConfirmationNumber(event.description || '') }]
        : []
    };
  }, [event]);
  
  const [details, setDetails] = useState<TransportationDetails>(getInitialDetails());
  
  // Handle field changes
  const handleChange = useCallback((field: keyof TransportationDetails, value: any) => {
    setDetails(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  
  // Save updated transportation
  const saveChanges = useCallback(async () => {
    if (!details.departureDate) {
      toast.error("Departure date is required");
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Format date for database
      const formattedDate = format(new Date(details.departureDate), 'yyyy-MM-dd');
      const typeDisplay = details.type.charAt(0).toUpperCase() + details.type.slice(1);
      
      // Build description with all details
      const description = buildEventDescription(details);
      
      const { error } = await supabase
        .from('itinerary_events')
        .update({
          title: `${typeDisplay} to ${details.arrival}${details.flightNumber ? ` (${details.flightNumber})` : ''}`,
          date: formattedDate,
          time: details.departureTime || null,
          location: `From: ${details.departure} To: ${details.arrival}`,
          description: description,
          transportation_type: details.type,
          user_name: details.userName
        })
        .eq('id', event.id);
        
      if (error) throw error;
      
      toast.success("Transportation details updated successfully");
      onClose();
    } catch (error) {
      console.error("Error updating transportation:", error);
      toast.error("Failed to update transportation details");
    } finally {
      setIsSaving(false);
    }
  }, [details, event.id, onClose]);
  
  return {
    details,
    handleChange,
    saveChanges,
    isSaving
  };
};

// Helper functions
function getCleanDescription(description: string): string {
  return description
    .replace(/\[TRANSPORTATION\]/g, '')
    .replace(/Confirmation: [A-Z0-9]+/g, '')
    .replace(/Type: [a-z]+/g, '')
    .trim();
}

function getConfirmationNumber(description: string): string {
  const match = description.match(/Confirmation: ([A-Z0-9]+)/);
  return match ? match[1] : '';
}
